import React, { useState } from "react";
import { Download, Loader2, Radio, Table2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { downloadBlob, errMsg, fmtDay, fmtINR } from "../../lib/api";

const PREVIEW_ROWS = 8;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Live · 12:45 IST" badge — every artifact is read from the database at the moment the question is answered. */
export const LiveBadge = ({ asOf, testId }) => {
  if (!asOf) return null;
  const m = /(\d{2}:\d{2})(?::\d{2})?\s*IST/.exec(String(asOf));
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--success))] shrink-0"
      title={`Read live from the database at ${asOf}`} data-testid={testId}>
      <Radio className="h-3 w-3" aria-hidden="true" /> Live{m ? ` · ${m[1]} IST` : ""}
    </span>
  );
};

export const formatCell = (value, type) => {
  if (value === null || value === undefined || value === "") return "—";
  switch (type) {
    case "inr": return typeof value === "number" ? fmtINR(value) : value;
    case "int": return typeof value === "number" ? value.toLocaleString("en-IN") : value;
    case "pct": return typeof value === "number" ? `${value.toFixed(1)}%` : value;
    case "date": return /^\d{4}-\d{2}-\d{2}/.test(String(value)) ? fmtDay(String(value)) : value;
    case "month": {
      const m = /^(\d{4})-(\d{2})$/.exec(String(value));
      return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : value;
    }
    default: return String(value);
  }
};

const NUMERIC = new Set(["inr", "int", "pct"]);

/** Exact report table produced by a backend tool; Excel export is served from the stored artifact. */
export const ArtifactTable = ({ artifact, code, testId = "artifact-table" }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const { columns = [], rows = [], types = [], title, row_count: rowCount, index, message_id: messageId, as_of: asOf } = artifact || {};
  const visible = expanded ? rows : rows.slice(0, PREVIEW_ROWS);
  const total = rowCount ?? rows.length;

  const exportXlsx = async () => {
    if (!messageId || index === undefined) { toast.error("Export is available once the answer is complete"); return; }
    setBusy(true);
    try {
      await downloadBlob(`/t/${code}/assistant/messages/${messageId}/artifacts/${index}/xlsx`, `${(title || "report").replace(/[^\w\- ]+/g, "").trim() || "report"}.xlsx`);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border bg-card shadow-sm overflow-hidden" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-muted/40">
        <div className="flex items-center gap-2 min-w-0">
          <Table2 className="h-4 w-4 text-primary shrink-0" aria-hidden="true" />
          <p className="text-xs font-semibold font-heading truncate" data-testid={`${testId}-title`} title={title}>{title || "Report"}</p>
          <span className="text-[11px] text-muted-foreground shrink-0 tabular-nums">{total.toLocaleString("en-IN")} row{total === 1 ? "" : "s"}</span>
          <LiveBadge asOf={asOf} testId={`${testId}-live`} />
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" onClick={exportXlsx} disabled={busy || !rows.length} data-testid={`${testId}-export`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />} Excel
        </Button>
      </div>
      <div className="overflow-x-auto max-h-80 overflow-y-auto">
        <Table className="text-xs">
          <TableHeader className="sticky top-0 bg-card z-[1]">
            <TableRow>
              {columns.map((c, i) => (
                <TableHead key={c + i} className={`h-8 px-2.5 whitespace-nowrap text-[11px] ${NUMERIC.has(types[i]) ? "text-right" : ""}`}>{c}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((r, ri) => (
              <TableRow key={ri} className="even:bg-muted/20" data-testid={`${testId}-row-${ri}`}>
                {r.map((v, ci) => (
                  <TableCell key={ci} className={`px-2.5 py-1.5 whitespace-nowrap ${NUMERIC.has(types[ci]) ? "text-right tabular-nums" : ""}`}>{formatCell(v, types[ci])}</TableCell>
                ))}
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow><TableCell colSpan={columns.length || 1} className="text-center text-muted-foreground py-4">No rows</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {rows.length > PREVIEW_ROWS && (
        <button type="button" onClick={() => setExpanded((e) => !e)} data-testid={`${testId}-toggle`}
          className="w-full text-xs text-primary font-medium py-1.5 border-t hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {expanded ? "Show fewer rows" : `Show all ${rows.length.toLocaleString("en-IN")} rows`}
        </button>
      )}
    </div>
  );
};
